import React, { useEffect, useRef, useState } from 'react'
import {
  getCurrentLanguage,
  setLanguage,
  SUPPORTED_LANGUAGES,
  LANGUAGE_EVENT,
  type LanguageOption,
} from '../utils/googleTranslate'
import FlagIcon from './FlagIcon'

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'desktop',
}) => {
  const [currentCode, setCurrentCode] = useState<string>(getCurrentLanguage())
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Keep internal state in sync with cookie/storage changes
    const onLanguageChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ langCode: string }>
      if (customEvent.detail?.langCode) {
        setCurrentCode(customEvent.detail.langCode)
      } else {
        setCurrentCode(getCurrentLanguage())
      }
    }

    window.addEventListener(LANGUAGE_EVENT, onLanguageChange)
    return () => {
      window.removeEventListener(LANGUAGE_EVENT, onLanguageChange)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const currentLang: LanguageOption =
    SUPPORTED_LANGUAGES.find(
      (l) =>
        l.code.toLowerCase() === currentCode.toLowerCase() ||
        (currentCode.toLowerCase().startsWith('zh') && l.code.startsWith('zh'))
    ) || SUPPORTED_LANGUAGES[0]

  const handleSelect = (lang: LanguageOption) => {
    setIsOpen(false)
    if (lang.code !== currentCode) {
      setLanguage(lang.code)
    }
  }

  if (variant === 'mobile') {
    return (
      <div ref={dropdownRef} className="relative notranslate">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center gap-[8px] rounded-full border border-gray-200 bg-white/80 px-3 py-1.5 shadow-sm active:scale-95 transition-all"
          aria-label="Select language"
          aria-expanded={isOpen}
        >
          <FlagIcon
            code={currentLang.code}
            className="h-[26px] w-[26px]"
            alt={currentLang.name}
          />
          <span className="font-poppins text-[16px] font-semibold leading-none text-[#444444]">
            {currentLang.badge}
          </span>
          <svg
            width="14"
            height="8"
            viewBox="0 0 15 9"
            fill="none"
            aria-hidden="true"
            className={`transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          >
            <path
              d="M1 1L7.5 7.5L14 1"
              stroke="#666A70"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-[240px] rounded-2xl border border-gray-100 bg-white p-1.5 shadow-[0_12px_32px_rgba(0,0,0,0.14)] z-[350] animate-in fade-in zoom-in-95 duration-150">
            <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Select Language
            </div>
            <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-50">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const isSelected =
                  lang.code.toLowerCase() === currentLang.code.toLowerCase() ||
                  (currentLang.code.toLowerCase().startsWith('zh') &&
                    lang.code.startsWith('zh'))
                return (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleSelect(lang)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                      isSelected
                        ? 'bg-[#EBF2FE] text-[#1E50BA] font-semibold'
                        : 'text-gray-700 hover:bg-gray-50 font-medium'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FlagIcon
                        code={lang.code}
                        className="h-5 w-5"
                        alt={lang.name}
                      />
                      <div className="flex flex-col">
                        <span className="text-[14px] leading-tight">
                          {lang.nativeName}
                        </span>
                        {lang.name !== lang.nativeName && (
                          <span className="text-[11px] text-gray-400 leading-tight">
                            {lang.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className={`text-[12px] font-bold px-2 py-0.5 rounded-md ${
                        isSelected
                          ? 'bg-[#1E50BA] text-white'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {lang.badge}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Desktop Variant
  return (
    <div ref={dropdownRef} className="relative notranslate">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="group flex items-center gap-[clamp(4px,0.55vw,8px)] py-1.5 px-2 rounded-full hover:bg-black/[0.03] transition-all cursor-pointer focus:outline-none"
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <FlagIcon
          code={currentLang.code}
          className="h-[clamp(16px,1.65vw,24px)] w-[clamp(16px,1.65vw,24px)]"
          alt={currentLang.name}
        />

        <span className="font-poppins text-[clamp(11px,1.05vw,16px)] font-semibold leading-none text-text-primary">
          {currentLang.badge}
        </span>

        <svg
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
          className={`h-[clamp(5px,0.6vw,7px)] w-[clamp(8px,0.85vw,12px)] transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        >
          <path
            d="M1 1L6 6L11 1"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-black"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-gray-100 bg-white p-1.5 shadow-[0_16px_36px_rgba(0,0,0,0.12)] z-[250] animate-in fade-in zoom-in-95 duration-150">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Select Language
          </div>
          <div className="max-h-[300px] overflow-y-auto divide-y divide-gray-50">
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isSelected =
                lang.code.toLowerCase() === currentLang.code.toLowerCase() ||
                (currentLang.code.toLowerCase().startsWith('zh') &&
                  lang.code.startsWith('zh'))
              return (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => handleSelect(lang)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-left transition-colors cursor-pointer ${
                    isSelected
                      ? 'bg-[#EBF2FE] text-[#1E50BA] font-semibold'
                      : 'text-gray-700 hover:bg-gray-50 font-medium'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <FlagIcon
                      code={lang.code}
                      className="h-5 w-5"
                      alt={lang.name}
                    />
                    <div className="flex flex-col">
                      <span className="text-[13px] leading-tight">
                        {lang.nativeName}
                      </span>
                      {lang.name !== lang.nativeName && (
                        <span className="text-[11px] text-gray-400 leading-tight">
                          {lang.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md ${
                      isSelected
                        ? 'bg-[#1E50BA] text-white'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {lang.badge}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageSelector
